import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, User, Tag, ChevronRight, Search, Clock, RefreshCw,
  AlertCircle, ArrowRight, Mail, Rss
} from 'lucide-react';
import { getAllPosts } from '../services/firebaseBlogService';
import { seedPosts } from '../data/seedPosts';
import SEO from '../components/SEO';

const SITE_URL = 'https://qsconn.com';

const CATEGORY_COLORS = {
  'Manufacturing': { bg: '#dc2626', text: '#fff', light: '#fee2e2' },
  'Technology': { bg: '#2563eb', text: '#fff', light: '#dbeafe' },
  'Industry Insights': { bg: '#7c3aed', text: '#fff', light: '#ede9fe' },
  'Sourcing Guide': { bg: '#ea580c', text: '#fff', light: '#ffedd5' },
  'Quality': { bg: '#16a34a', text: '#fff', light: '#dcfce7' },
};

const getCategoryStyle = (category, theme) => {
  const colors = CATEGORY_COLORS[category] || { bg: '#6b7280', text: '#fff', light: theme === 'dark' ? '#374151' : '#f3f4f6' };
  return {
    backgroundColor: colors.bg,
    color: colors.text,
  };
};

const getCategoryLightStyle = (category, theme) => {
  const colors = CATEGORY_COLORS[category] || { bg: '#6b7280', text: '#fff', light: theme === 'dark' ? '#374151' : '#f3f4f6' };
  return {
    backgroundColor: colors.light,
    color: colors.bg,
  };
};

const TARGET_KEYWORDS = {
  secondary: ['custom connectors', 'cable assembly', 'injection molding', 'stamping services', 'mold development', 'CNC machining', 'electronics manufacturing China'],
  longTail: ['connector manufacturer China', 'automotive connector supplier', 'industrial connector alternatives', 'TE connector replacement', 'Molex equivalent connector']
};

// Skeleton loading card
const SkeletonCard = ({ theme }) => (
  <div className={`rounded-3xl overflow-hidden border-2 ${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-white border-gray-200/60'}`}>
    <div className="aspect-[16/10] skeleton-shimmer" />
    <div className="p-8 space-y-4">
      <div className="skeleton-box h-4 w-20 rounded-full" />
      <div className="skeleton-box h-6 w-full rounded-lg" />
      <div className="skeleton-box h-6 w-4/5 rounded-lg" />
      <div className="skeleton-box h-4 w-full rounded" />
      <div className="skeleton-box h-4 w-3/4 rounded" />
      <div className="pt-4 border-t border-gray-200 dark:border-white/5 flex gap-4">
        <div className="skeleton-box h-3 w-24 rounded" />
        <div className="skeleton-box h-3 w-20 rounded" />
      </div>
    </div>
  </div>
);

// Newsletter section
const NewsletterSection = ({ theme }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
    }
  };

  return (
    <section className="py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <div className={`rounded-3xl p-10 md:p-14 text-center relative overflow-hidden ${
          theme === 'dark'
            ? 'bg-gradient-to-br from-gray-900 via-gray-950 to-black border border-white/5'
            : 'bg-gradient-to-br from-gray-50 via-white to-red-50 border border-gray-200 shadow-lg'
        }`}>
          {/* Decorative background */}
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-red-600/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-blue-600/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10">
            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6 ${
              theme === 'dark' ? 'bg-red-900/30' : 'bg-red-100'
            }`}>
              <Mail className={`w-7 h-7 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`} />
            </div>

            <h2 className={`text-2xl md:text-3xl font-extrabold mb-3 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Stay Updated on Connector Sourcing
            </h2>
            <p className={`text-base mb-8 max-w-lg mx-auto leading-relaxed ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Get weekly insights on connector sourcing, cost-saving strategies, and industry trends. Written by engineers, for engineers.
            </p>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                <input
                  type="email"
                  placeholder="Enter your work email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={`flex-1 px-5 py-3.5 rounded-2xl border-2 focus:outline-none transition-all font-medium ${
                    theme === 'dark'
                      ? 'bg-black/60 border-white/10 text-white placeholder-gray-500 focus:border-red-600/60'
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-red-600/50'
                  }`}
                />
                <button
                  type="submit"
                  className="px-8 py-3.5 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-2xl hover:from-red-700 hover:to-red-800 transition-all shadow-lg shadow-red-600/20 hover:shadow-red-600/30 whitespace-nowrap flex items-center justify-center gap-2"
                >
                  Subscribe
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            ) : (
              <div className={`flex items-center justify-center gap-3 p-4 rounded-2xl ${
                theme === 'dark' ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-700'
              }`}>
                <span className="text-2xl">✅</span>
                <p className="font-semibold">You're subscribed! Check your inbox for a confirmation email.</p>
              </div>
            )}

            <p className={`text-xs mt-5 tracking-wide ${
              theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              No spam, ever. Unsubscribe at any time. Read by 3,200+ procurement engineers worldwide.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Blog = ({ theme }) => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('newest');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const postsPerPage = 6;
  const filterBarRef = useRef(null);
  const [filterSticky, setFilterSticky] = useState(false);

  // Handle sticky filter bar
  useEffect(() => {
    const handleScroll = () => {
      if (filterBarRef.current) {
        const rect = filterBarRef.current.getBoundingClientRect();
        setFilterSticky(rect.top <= 80);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch posts
  useEffect(() => {
    let isMounted = true;
    const fetchPosts = async () => {
      setIsLoading(true);
      setError(null);

      const timeoutId = setTimeout(() => {
        if (isMounted) {
          setError('Connection timeout. Please check your internet and try again.');
          setIsLoading(false);
        }
      }, 15000);

      try {
        const posts = await getAllPosts();
        if (isMounted) {
          // Fallback to seed posts if Firebase returns empty
          if (!posts || posts.length === 0) {
            setBlogPosts(seedPosts);
          } else {
            // Combine Firebase posts with seed posts (avoiding duplicates)
            const combined = [...posts];
            seedPosts.forEach(sp => {
              if (!combined.some(p => p.id === sp.id || p.slug === sp.slug)) {
                combined.push(sp);
              }
            });
            setBlogPosts(combined);
          }
        }
      } catch (err) {
        console.error('Error fetching posts:', err);
        if (isMounted) {
          // Use seed posts as fallback on error
          setBlogPosts(seedPosts);
        }
      } finally {
        clearTimeout(timeoutId);
        if (isMounted) setIsLoading(false);
      }
    };
    fetchPosts();
    return () => { isMounted = false; };
  }, []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ['All', ...new Set(blogPosts.map(post => post.category).filter(Boolean))];
    return cats;
  }, [blogPosts]);

  // Filter and sort posts
  const filteredPosts = useMemo(() => {
    let filtered = [...blogPosts];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post =>
        post.title?.toLowerCase().includes(query) ||
        post.excerpt?.toLowerCase().includes(query) ||
        post.author?.toLowerCase().includes(query) ||
        post.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest': return new Date(a.date) - new Date(b.date);
        case 'title': return (a.title || '').localeCompare(b.title || '');
        default: return new Date(b.date) - new Date(a.date);
      }
    });

    return filtered;
  }, [blogPosts, searchQuery, selectedCategory, sortBy]);

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, selectedCategory, sortBy]);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const featuredPost = filteredPosts[0];
  const remainingPosts = filteredPosts.slice(1);

  // JSON-LD CollectionPage schema
  const collectionPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Connector Manufacturing Knowledge Hub | Qingshun Electronics Blog",
    "description": "Expert connector guides, manufacturing insights, and technical articles for electronics professionals. Learn how to source cost-effective connectors from China.",
    "url": `${SITE_URL}/blog`,
    "publisher": {
      "@type": "Organization",
      "name": "Qingshun Electronics",
      "url": SITE_URL,
    },
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": filteredPosts.length,
      "itemListElement": filteredPosts.slice(0, 10).map((post, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `${SITE_URL}/blog/${post.slug || post.id}`,
        "name": post.title,
      }))
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (newsletterEmail) {
      setNewsletterSubmitted(true);
    }
  };

  return (
    <>
      <SEO
        title="Knowledge Hub & Technical Blog | Connector Guides & Manufacturing Insights"
        description="Expert connector guides, manufacturing insights, and technical articles for electronics professionals. Learn how to source cost-effective TE, Molex, Amphenol alternatives from China's leading connector manufacturer. Updated weekly."
        url="/blog"
        keywords="connector alternatives blog, TE equivalent guide, Molex replacement, Amphenol alternative, connector manufacturing guide, cable assembly guide, injection molding guide, electronics manufacturing China, connector technology trends"
        type="website"
        image="/logo512.png"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Blog", url: "/blog" }
        ]}
        extraStructuredDataArray={[
          collectionPageSchema,
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE_URL}/blog` }
            ]
          }
        ]}
      />

      {/* Animated gradient hero */}
      <div className="absolute top-0 left-0 w-full overflow-hidden pointer-events-none" style={{ height: '720px', zIndex: 0 }}>
        <div
          className="hero-gradient absolute top-0 left-0 w-full h-full"
          style={{
            background: theme === 'dark'
              ? 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(220,38,38,0.08) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 80% 20%, rgba(220,38,38,0.04) 0%, transparent 50%)'
              : 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(220,38,38,0.06) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 80% 20%, rgba(220,38,38,0.03) 0%, transparent 50%)'
          }}
        />
      </div>

      {/* Hero Section */}
      <section className={`relative pt-36 pb-20 px-4 overflow-hidden z-10 transition-colors duration-500 ${
        theme === 'dark'
          ? 'bg-gradient-to-b from-black via-gray-950/20 to-black'
          : 'bg-gradient-to-b from-gray-50/50 to-white'
      }`}>
        <div className="max-w-6xl mx-auto text-center">

          {/* Animated badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-6 tracking-widest uppercase animate-pulse-subtle"
            style={{
              border: theme === 'dark' ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(220,38,38,0.15)',
              background: theme === 'dark' ? 'rgba(220,38,38,0.06)' : 'rgba(220,38,38,0.04)',
              color: '#dc2626',
              animation: 'pulse 3s ease-in-out infinite'
            }}>
            <Rss className="w-3 h-3" />
            <span>Industry Insights &amp; Technical Guides</span>
          </div>

          <h1 className={`text-5xl md:text-6xl font-extrabold mb-6 tracking-tight transition-colors duration-500 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Connector Manufacturing
            <span className="bg-gradient-to-r from-red-500 via-red-600 to-pink-600 bg-clip-text text-transparent"> Knowledge Hub</span>
          </h1>

          <p className={`text-xl mb-3 max-w-2xl mx-auto leading-relaxed transition-colors duration-500 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Expert technical guides on connector selection, manufacturing processes, and cost-saving strategies for procurement engineers and sourcing professionals.
          </p>

          <p className={`text-sm mb-10 max-w-3xl mx-auto leading-relaxed transition-colors duration-500 ${
            theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
          }`}>
            <strong>Topics:</strong> Custom connector manufacturing · Cable assembly services · Injection molding · Stamping · Mold development · TE/Molex/Amphenol alternatives · Automotive &amp; industrial connectors
          </p>
        </div>
      </section>

      {/* Sticky Search/Filter Bar */}
      <div className={`relative z-30 px-4 transition-all duration-300 ${
        filterSticky ? 'sticky top-0 pt-2 pb-2' : ''
      }`}
      style={{ top: filterSticky ? '72px' : 'auto' }}>
        <div className={`max-w-6xl mx-auto p-4 rounded-2xl backdrop-blur-xl border transition-all duration-300 ${
          filterSticky
            ? theme === 'dark'
              ? 'bg-black/90 border-white/10 shadow-2xl'
              : 'bg-white/95 border-gray-200/80 shadow-xl'
            : theme === 'dark'
              ? 'bg-white/5 border-white/10'
              : 'bg-white/90 border-gray-200/60'
        }`}
        ref={filterBarRef}
        id="filter-bar">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search input */}
            <div className="flex-1 relative">
              <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <input
                type="text"
                placeholder="Search articles, topics, guides..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-12 pr-4 py-3 rounded-2xl border-2 focus:outline-none transition-all ${
                  theme === 'dark'
                    ? 'bg-black/40 border-gray-800 text-white placeholder-gray-600 focus:border-red-600/50'
                    : 'bg-gray-50 border-gray-200/60 text-gray-900 placeholder-gray-400 focus:border-red-600/50'
                }`}
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`px-4 py-3 rounded-2xl border-2 focus:outline-none cursor-pointer font-medium transition-all ${
                theme === 'dark'
                  ? 'bg-black/40 border-gray-800 text-gray-300 focus:border-red-600/50 hover:bg-black/60'
                  : 'bg-gray-50 border-gray-200/60 text-gray-700 focus:border-red-600/50 hover:bg-gray-100'
              }`}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`px-4 py-3 rounded-2xl border-2 focus:outline-none cursor-pointer font-medium transition-all ${
                theme === 'dark'
                  ? 'bg-black/40 border-gray-800 text-gray-300 focus:border-red-600/50 hover:bg-black/60'
                  : 'bg-gray-50 border-gray-200/60 text-gray-700 focus:border-red-600/50 hover:bg-gray-100'
              }`}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="title">By Title</option>
            </select>
          </div>

          {/* Results count */}
          <p className={`text-xs font-semibold mt-3 tracking-wide ${
            theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
          }`}>
            ⚡ Showing {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'}
          </p>
        </div>
      </div>

      {/* Blog Articles Grid Section */}
      <section className={`py-16 px-4 z-10 relative transition-colors duration-500 ${
        theme === 'dark' ? 'bg-black' : 'bg-gray-50/30'
      }`}>
        <div className="max-w-6xl mx-auto" id="blog-posts-section">

          {/* Loading State */}
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => <SkeletonCard key={i} theme={theme} />)}
            </div>
          )}

          {/* Error State */}
          {!isLoading && error && (
            <div className={`text-center py-20 rounded-3xl border-2 ${
              theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-white border-gray-200'
            }`}>
              <AlertCircle className={`w-16 h-16 mx-auto mb-5 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`} />
              <p className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{error}</p>
              <p className={`text-sm mb-8 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Please check your connection and try again.</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-2xl hover:from-red-700 hover:to-red-800 transition shadow-lg shadow-red-600/30"
              >
                <RefreshCw className="w-4 h-4" /> Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredPosts.length === 0 && (
            <div className={`text-center py-24 rounded-3xl border-2 ${
              theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-white border-gray-200'
            }`}>
              {/* Illustration-like empty state */}
              <div className="mb-8">
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
                  <circle cx="60" cy="60" r="56" fill={theme === 'dark' ? '#1f2937' : '#f3f4f6'} />
                  <rect x="35" y="30" width="50" height="60" rx="4" fill={theme === 'dark' ? '#374151' : '#e5e7eb'} />
                  <rect x="40" y="38" width="40" height="4" rx="2" fill={theme === 'dark' ? '#4b5563' : '#d1d5db'} />
                  <rect x="40" y="48" width="30" height="3" rx="1.5" fill={theme === 'dark' ? '#4b5563' : '#d1d5db'} />
                  <rect x="40" y="56" width="35" height="3" rx="1.5" fill={theme === 'dark' ? '#4b5563' : '#d1d5db'} />
                  <rect x="40" y="64" width="25" height="3" rx="1.5" fill={theme === 'dark' ? '#4b5563' : '#d1d5db'} />
                  <circle cx="60" cy="80" r="12" fill={theme === 'dark' ? '#dc2626' : '#ef4444'} opacity="0.2" />
                  <path d="M54 80h12M60 74v12" stroke={theme === 'dark' ? '#dc2626' : '#ef4444'} strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className={`text-2xl font-extrabold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                No articles yet
              </h3>
              <p className={`text-base mb-8 max-w-sm mx-auto ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                The knowledge hub is waiting for its first article. Start sharing your expertise with the community.
              </p>
              <Link
                to="/admin"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-2xl hover:from-red-700 hover:to-red-800 transition-all shadow-lg shadow-red-600/30 hover:shadow-red-600/40 transform hover:scale-[1.02]"
              >
                ✍️ Write the First Post
              </Link>
            </div>
          )}

          {/* Featured Post Hero Card */}
          {!isLoading && !error && featuredPost && selectedCategory === 'All' && !searchQuery && (
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <span className={`px-4 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider ${
                  theme === 'dark' ? 'bg-red-900/30 text-red-400 border border-red-800/30' : 'bg-red-100 text-red-700 border border-red-200'
                }`}>
                  ⭐ Featured Article
                </span>
              </div>

              <Link
                to={`/blog/${featuredPost.slug || featuredPost.id}`}
                className={`block group rounded-3xl overflow-hidden border-2 transition-all duration-500 hover:scale-[1.01] hover:border-red-600/30 hover:shadow-2xl hover:shadow-red-600/5 ${
                  theme === 'dark'
                    ? 'bg-white/5 border-white/5'
                    : 'bg-white border-gray-200/60 shadow-xl shadow-gray-200/10'
                }`}
              >
                <div className="flex flex-col lg:flex-row">
                  {/* Featured Image */}
                  {featuredPost.image && (
                    <div className="lg:w-[55%] relative overflow-hidden lg:min-h-[380px]">
                      <img
                        src={featuredPost.image}
                        alt={featuredPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[800ms]"
                        onError={(e) => { e.target.src = 'https://picsum.photos/800/500?blur=2'; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />

                      {/* Category badge overlay */}
                      {featuredPost.category && (
                        <span
                          className="absolute top-5 left-5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-md"
                          style={getCategoryLightStyle(featuredPost.category, theme)}
                        >
                          {featuredPost.category}
                        </span>
                      )}

                      {/* Read time overlay */}
                      {featuredPost.readTime && (
                        <span className="absolute bottom-5 left-5 px-3 py-1 rounded-full text-xs font-semibold bg-black/50 text-white backdrop-blur-md flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" /> {featuredPost.readTime}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Featured Content */}
                  <div className="lg:w-[45%] p-8 md:p-12 flex flex-col justify-center">
                    <h2 className={`text-2xl md:text-3xl lg:text-4xl font-extrabold mb-5 leading-tight group-hover:text-red-500 transition duration-300 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {featuredPost.title}
                    </h2>

                    <p className={`text-base mb-8 leading-relaxed line-clamp-4 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {featuredPost.excerpt}
                    </p>

                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className={`flex items-center flex-wrap gap-5 text-sm ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                        <span className="flex items-center gap-2 font-medium">
                          <User className="w-4 h-4" /> {featuredPost.author}
                        </span>
                        <span className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> {featuredPost.date}
                        </span>
                      </div>

                      <span className={`inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-bold rounded-xl group-hover:from-red-700 group-hover:to-red-800 transition-all shadow-md shadow-red-600/20`}>
                        Read more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Article Grid */}
          {!isLoading && !error && currentPosts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug || post.id}`}
                  className={`group flex flex-col rounded-3xl overflow-hidden border-2 transition-all duration-500 hover:-translate-y-2 hover:border-red-600/30 hover:shadow-2xl hover:shadow-red-600/5 ${
                    theme === 'dark'
                      ? 'bg-white/5 border-white/5'
                      : 'bg-white border-gray-200/60 shadow-xl shadow-gray-200/10'
                  }`}
                >
                  {/* Card Image */}
                  {post.image && (
                    <div className="relative overflow-hidden aspect-[16/10]">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[700ms]"
                        onError={(e) => { e.target.src = 'https://picsum.photos/500/320?blur=2'; }}
                      />
                      {/* Category badge */}
                      {post.category && (
                        <span
                          className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-md"
                          style={getCategoryLightStyle(post.category, theme)}
                        >
                          {post.category}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="p-6 md:p-8 flex flex-col flex-1">
                    {/* Title */}
                    <h3 className={`text-lg font-bold mb-3 leading-snug group-hover:text-red-500 transition duration-300 line-clamp-2 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {post.title}
                    </h3>

                    {/* Excerpt */}
                    <p className={`text-sm mb-6 leading-relaxed line-clamp-2 flex-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {post.excerpt}
                    </p>

                    {/* Meta row */}
                    <div className={`flex items-center justify-between pt-4 border-t text-xs ${
                      theme === 'dark' ? 'border-white/5 text-gray-500' : 'border-gray-100 text-gray-500'
                    }`}>
                      <span className="flex items-center gap-1.5 font-medium">
                        {/* Avatar placeholder */}
                        <span className="w-6 h-6 rounded-full bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                          {post.author ? post.author.charAt(0).toUpperCase() : 'Q'}
                        </span>
                        <span className="hidden sm:inline">{post.author}</span>
                      </span>

                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {post.date}
                        </span>
                        {post.readTime && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {post.readTime}
                          </span>
                        )}
                        {/* Category dot */}
                        {post.category && (
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: CATEGORY_COLORS[post.category]?.bg || '#6b7280' }}
                            title={post.category}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!isLoading && !error && totalPages > 1 && (
            <div className="flex justify-center items-center gap-3 mt-16">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`w-11 h-11 flex items-center justify-center rounded-2xl border-2 transition-all duration-300 ${
                  currentPage === 1
                    ? 'opacity-40 cursor-not-allowed border-transparent'
                    : theme === 'dark'
                      ? 'border-white/5 bg-white/5 text-white hover:bg-white/10 hover:border-white/10'
                      : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-100'
                }`}
              >
                ←
              </button>

              {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;

                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`w-11 h-11 rounded-2xl font-bold transition-all duration-300 ${
                      currentPage === pageNum
                        ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-600/30 border-transparent'
                        : theme === 'dark'
                          ? 'border-2 border-white/5 bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                          : 'border-2 border-gray-200 bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`w-11 h-11 flex items-center justify-center rounded-2xl border-2 transition-all duration-300 ${
                  currentPage === totalPages
                    ? 'opacity-40 cursor-not-allowed border-transparent'
                    : theme === 'dark'
                      ? 'border-white/5 bg-white/5 text-white hover:bg-white/10 hover:border-white/10'
                      : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-100'
                }`}
              >
                →
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      {!isLoading && !error && filteredPosts.length > 0 && (
        <NewsletterSection theme={theme} />
      )}

      {/* B2B Sourcing CTA Section */}
      {!isLoading && !error && filteredPosts.length > 0 && (
        <section className={`py-20 px-4 z-10 relative transition-colors duration-500 ${
          theme === 'dark' ? 'bg-black' : 'bg-white'
        }`}>
          <div className="max-w-5xl mx-auto">
            {/* Decorative elements */}
            <div className={`absolute left-0 right-0 h-px ${
              theme === 'dark' ? 'bg-gradient-to-r from-transparent via-red-900/20 to-transparent' : 'bg-gradient-to-r from-transparent via-red-200/40 to-transparent'
            }`} />

            <div className={`rounded-3xl p-10 md:p-16 relative overflow-hidden mt-[-1px] ${
              theme === 'dark'
                ? 'bg-gradient-to-br from-red-950/30 via-gray-950 to-gray-950 border border-red-900/15'
                : 'bg-gradient-to-br from-red-50 via-white to-orange-50 border border-red-100 shadow-xl'
            }`}>

              {/* Animated glow decorations */}
              <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/5 rounded-full blur-3xl pointer-events-none animate-pulse-slow" />
              <div className="absolute bottom-0 left-0 w-72 h-72 bg-orange-500/4 rounded-full blur-3xl pointer-events-none" />

              <div className="relative z-10 text-center">
                <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold mb-6 tracking-wider uppercase ${
                  theme === 'dark' ? 'bg-red-900/30 text-red-400 border border-red-800/30' : 'bg-red-100 text-red-700 border border-red-200'
                }`}>
                  🏭 For Procurement &amp; Sourcing Teams
                </div>

                <h2 className={`text-3xl md:text-4xl font-extrabold mb-4 tracking-tight ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Looking for a Reliable{' '}
                  <span className={theme === 'dark' ? 'text-red-400' : 'text-red-600'}>Connector Manufacturer</span>?
                </h2>

                <p className={`text-lg max-w-2xl mx-auto mb-10 leading-relaxed ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Qingshun Electronics manufactures custom connectors, cable assemblies, and precision stamped parts for companies worldwide. ISO 9001:2015 certified. 14+ years experience. Direct factory pricing.
                </p>

                {/* Value props grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-12">
                  {[
                    { icon: '💰', label: '50–70% Cost Savings', desc: 'vs TE/Molex/Amphenol' },
                    { icon: '⚡', label: '2–4 Week Lead Time', desc: 'production orders' },
                    { icon: '✅', label: 'ISO 9001:2015', desc: 'certified quality' },
                    { icon: '🌍', label: '1000+ Clients', desc: 'in 8+ countries' },
                  ].map((item, i) => (
                    <div key={i} className={`rounded-2xl p-5 text-center transition-all duration-300 hover:scale-[1.03] ${
                      theme === 'dark' ? 'bg-white/5 border border-white/5 hover:bg-white/8' : 'bg-white border border-gray-100 shadow-sm hover:shadow-md'
                    }`}>
                      <div className="text-3xl mb-2">{item.icon}</div>
                      <div className={`text-sm font-extrabold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{item.label}</div>
                      <div className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>{item.desc}</div>
                    </div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-xl hover:from-red-700 hover:to-red-800 transition-all shadow-lg shadow-red-600/25 hover:shadow-red-600/40 transform hover:scale-[1.02]"
                  >
                    📧 Request a Free Quote <ChevronRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/services"
                    className={`inline-flex items-center justify-center gap-2 px-8 py-4 font-bold rounded-xl border-2 transition-all ${
                      theme === 'dark'
                        ? 'border-red-800/40 text-red-400 hover:bg-red-950/30'
                        : 'border-red-200 text-red-600 hover:bg-red-50'
                    }`}
                  >
                    View Manufacturing Services
                  </Link>
                </div>

                {/* Trust signals */}
                <div className={`mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs tracking-wide ${
                  theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  <span>✓ Free samples available</span>
                  <span>✓ Compatibility testing included</span>
                  <span>✓ Response within 24 hours</span>
                  <span>✓ NDA friendly</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Topic Tags Section */}
      {!isLoading && !error && (
        <section className={`py-12 px-4 z-10 relative border-t ${
          theme === 'dark' ? 'bg-black border-white/5' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="max-w-5xl mx-auto">
            <h2 className={`text-center text-sm font-bold uppercase tracking-widest mb-6 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }`}>
              Popular Topics for Sourcing Professionals
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {[...TARGET_KEYWORDS.secondary, ...TARGET_KEYWORDS.longTail].map((kw) => (
                <Link
                  key={kw}
                  to={`/blog?search=${encodeURIComponent(kw)}`}
                  className={`px-4 py-2 rounded-xl text-xs font-medium transition-all hover:-translate-y-0.5 ${
                    theme === 'dark'
                      ? 'bg-white/5 text-gray-400 hover:bg-red-950/20 hover:text-red-400 border border-white/5'
                      : 'bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 border border-gray-200 shadow-sm'
                  }`}
                >
                  {kw}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Skeleton animation styles */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .skeleton-shimmer {
          background: linear-gradient(90deg, ${theme === 'dark' ? '#1f2937' : '#e5e7eb'} 25%, ${theme === 'dark' ? '#374151' : '#f3f4f6'} 50%, ${theme === 'dark' ? '#1f2937' : '#e5e7eb'} 75%);
          background-size: 400px 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }
        .skeleton-box {
          background: linear-gradient(90deg, ${theme === 'dark' ? '#1f2937' : '#e5e7eb'} 25%, ${theme === 'dark' ? '#374151' : '#f3f4f6'} 50%, ${theme === 'dark' ? '#1f2937' : '#e5e7eb'} 75%);
          background-size: 400px 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }
        @keyframes pulse-subtle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 3s ease-in-out infinite;
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default Blog;
